<script setup>
import { ref, computed, nextTick, watch, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useAccessStore } from '@/stores/access'
import { Paperclip, MapPin, X, ArrowUp, ChartLine, MapPinned, ImagePlus, Smile } from 'lucide-vue-next'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/toast'
import EmojiPicker from '@/components/ui/EmojiPicker.vue'
import { validateFile } from '@/config/media'
import { generateThumbnail, getAcceptString } from '@/utils/fileValidation'
import { uploadResource } from '@/services/resourcesApi'
import ChatAvatar from './ChatAvatar.vue'
import { cardsFromText } from '@/utils/chatCards'

// Mirrors apiv3 app/schemas/conversation.py: one picture per message, and at
// most MAX_ATTACHMENTS entries in total once unfurled cards are counted.
const MAX_IMAGES = 1
const MAX_ATTACHMENTS = 8

const props = defineProps({
  members: { type: Array, default: () => [] },
  draftLocation: { type: Array, default: null },
  // Off outside a project: there is no map to pin to and no project data to
  // attach, so the paperclip goes straight to the file dialog.
  dataAttachments: { type: Boolean, default: true },
  // Off in the on-map compose bubble, where the pin already is the location.
  pinOption: { type: Boolean, default: true },
  placeholder: { type: String, default: 'Message — @ to mention' },
})

const emit = defineEmits(['send', 'clear-location', 'pin-location'])

const text = ref('')
const mentions = ref([])
const attachments = ref([])
const textareaEl = ref(null)
const fileInputEl = ref(null)
const imageAccept = getAcceptString(['image'])

// @mention autocomplete: a plain panel above the textarea so focus stays put
const mentionOpen = ref(false)
const mentionQuery = ref('')
const mentionStart = ref(0)
const highlightIndex = ref(0)

const mentionCandidates = computed(() => {
  const q = mentionQuery.value.toLowerCase()
  return props.members
    .filter(
      (m) =>
        (m.nickname && m.nickname.includes(q)) ||
        (m.display_name && m.display_name.toLowerCase().includes(q))
    )
    .slice(0, 20)
})

const mentionItemEls = ref([])
watch(mentionCandidates, () => {
  mentionItemEls.value = []
})
watch([highlightIndex, mentionOpen], () => {
  nextTick(() => mentionItemEls.value[highlightIndex.value]?.scrollIntoView({ block: 'nearest' }))
})

function updateMention() {
  const el = textareaEl.value
  if (!el) return
  const upto = text.value.slice(0, el.selectionStart)
  const match = upto.match(/@([a-zA-Z0-9._]*)$/)
  if (match) {
    mentionOpen.value = true
    mentionQuery.value = match[1]
    mentionStart.value = upto.length - match[0].length
    highlightIndex.value = 0
  } else {
    mentionOpen.value = false
  }
}

function pickMention(member) {
  const el = textareaEl.value
  const before = text.value.slice(0, mentionStart.value)
  const after = text.value.slice(el?.selectionStart ?? text.value.length)
  text.value = `${before}@${member.nickname} ${after}`
  if (!mentions.value.some((m) => m.member_id === member.member_id)) {
    mentions.value.push({
      member_id: member.member_id,
      display_name: member.display_name,
      nickname: member.nickname,
      agent_id: member.agent_id,
    })
  }
  mentionOpen.value = false
  nextTick(() => {
    const caret = before.length + member.nickname.length + 2
    el?.focus()
    el?.setSelectionRange(caret, caret)
  })
}

function onKeydown(event) {
  if (mentionOpen.value && mentionCandidates.value.length) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      highlightIndex.value = (highlightIndex.value + 1) % mentionCandidates.value.length
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      highlightIndex.value =
        (highlightIndex.value - 1 + mentionCandidates.value.length) % mentionCandidates.value.length
      return
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault()
      pickMention(mentionCandidates.value[highlightIndex.value])
      return
    }
    if (event.key === 'Escape') {
      // Contained: closing the mention list must not also cancel a map draft.
      event.preventDefault()
      event.stopPropagation()
      mentionOpen.value = false
      return
    }
  }
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    send()
  }
}

// Images upload through the shared resources stack as soon as they are
// attached; the blob URL is only the preview while the upload runs. The sent
// attachment carries the resource UUID and the server variant URLs.
async function addFiles(files) {
  const imageCount = attachments.value.filter((a) => a.type === 'image').length
  const accepted = Array.from(files).slice(0, Math.max(MAX_IMAGES - imageCount, 0))
  if (files.length > accepted.length) {
    toast({
      title: 'One image per message',
      description: 'Send the picture, then attach the next one.',
      variant: 'destructive',
    })
  }

  for (const file of accepted) {
    const validation = validateFile(file)
    if (!validation.valid) {
      toast({ title: 'Invalid file', description: `${file.name}: ${validation.error}`, variant: 'destructive' })
      continue
    }
    if (validation.type !== 'image') {
      toast({ title: 'Images only', description: `${file.name} is not an image.`, variant: 'destructive' })
      continue
    }

    const url = URL.createObjectURL(file)
    const thumbnail = await generateThumbnail(file).catch(() => null)
    const attachment = {
      type: 'image',
      file_type: 'image',
      filename: file.name,
      mime: file.type,
      sizebytes: file.size,
      url,
      variant_urls: { thumbnail: thumbnail || url, fullsize: url },
      uploading: true,
      resource_uuid: null,
    }
    attachments.value.push(attachment)

    uploadResource(file)
      .then((data) => {
        const variants = Object.fromEntries((data.resources || []).map((r) => [r.type, r.url]))
        const fullsize = variants.fullsize || variants.original || attachment.url
        revokeImage(attachment)
        attachment.resource_uuid = data.uuid
        attachment.url = fullsize
        attachment.variant_urls = {
          thumbnail: variants.thumbnail || variants.small || fullsize,
          fullsize,
        }
        attachment.uploading = false
      })
      .catch((e) => {
        const index = attachments.value.indexOf(attachment)
        if (index >= 0) removeAttachment(index)
        toast({ title: 'Upload failed', description: `${file.name}: ${e.message}`, variant: 'destructive' })
      })
  }
}

function insertEmoji(emoji) {
  if (!emoji) return
  const el = textareaEl.value
  const start = el?.selectionStart ?? text.value.length
  const end = el?.selectionEnd ?? start
  text.value = text.value.slice(0, start) + emoji + text.value.slice(end)
  nextTick(() => {
    const caret = start + emoji.length
    el?.focus()
    el?.setSelectionRange(caret, caret)
  })
}

function openFileDialog() {
  fileInputEl.value?.click()
}

async function onFileSelect(event) {
  await addFiles(event.target.files)
  event.target.value = ''
}

async function onPaste(event) {
  const files = Array.from(event.clipboardData?.files || []).filter((f) => f.type.startsWith('image/'))
  if (files.length) {
    event.preventDefault()
    await addFiles(files)
    return
  }
  // Pasted in-app URLs unfurl into cards (the URL text stays in the message).
  const pasted = event.clipboardData?.getData('text') || ''
  if (pasted.includes('://')) unfurlUrls(pasted)
}

const router = useRouter()
const accessStore = useAccessStore()
const memberRole = (member) => member.role || accessStore.role(member.role_id)?.name

async function unfurlUrls(pasted) {
  const cards = await cardsFromText(pasted, router)
  for (const card of cards) {
    const duplicate = attachments.value.some(
      (a) => a.type === card.type && JSON.stringify(a) === JSON.stringify(card)
    )
    if (!duplicate && attachments.value.length < MAX_ATTACHMENTS) {
      attachments.value.push(card)
    }
  }
}

function revokeImage(attachment) {
  if (attachment.type !== 'image') return
  if (attachment.variant_urls?.thumbnail && attachment.variant_urls.thumbnail !== attachment.url) {
    URL.revokeObjectURL(attachment.variant_urls.thumbnail)
  }
  URL.revokeObjectURL(attachment.url)
}

// Sent attachments are owned by the store, so only still-pending blobs are freed here.
onBeforeUnmount(() => attachments.value.forEach(revokeImage))

function removeAttachment(index) {
  const [removed] = attachments.value.splice(index, 1)
  if (removed) revokeImage(removed)
}

function attachmentLabel(attachment) {
  if (attachment.type === 'metric') return attachment.title
  if (attachment.type === 'member') return attachment.display_name
  if (attachment.type === 'feature_list') return attachment.title || 'Features'
  if (attachment.type === 'insight') return attachment.title || 'Insight'
  return attachment.name
}

// Mentions picked from the autocomplete plus any @Name typed or pasted as
// plain text — the server only triggers agents from structured mentions, so a
// copy-pasted "@Analyst ..." must resolve to a mention entry too.
function collectMentions(trimmed) {
  const found = mentions.value.filter((m) => trimmed.includes(`@${m.nickname || m.display_name}`))
  for (const member of props.members) {
    if (found.some((m) => m.member_id === member.member_id)) continue
    const handles = [member.nickname, member.display_name].filter(Boolean)
    const hit = handles.some((handle) => {
      const escaped = handle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return new RegExp(`@${escaped}(?![a-zA-Z0-9._])`, 'i').test(trimmed)
    })
    if (hit) {
      found.push({
        member_id: member.member_id,
        display_name: member.display_name,
        nickname: member.nickname,
        agent_id: member.agent_id,
      })
    }
  }
  return found
}

function send() {
  const trimmed = text.value.trim()
  if (!trimmed && !attachments.value.length) return
  if (attachments.value.some((a) => a.uploading)) {
    toast({ title: 'Still uploading', description: 'Wait for images to finish uploading.' })
    return
  }
  emit('send', {
    text: trimmed,
    mentions: collectMentions(trimmed),
    attachments: attachments.value.map(({ uploading, ...attachment }) => attachment),
  })
  text.value = ''
  mentions.value = []
  attachments.value = []
  mentionOpen.value = false
}

defineExpose({ addFiles, focus: () => textareaEl.value?.focus() })
</script>

<template>
  <div class="relative">
    <div
      v-if="mentionOpen && mentionCandidates.length"
      class="absolute bottom-full left-0 z-30 mb-1 max-h-64 w-80 overflow-y-auto rounded-lg border bg-popover shadow-lg"
    >
      <button
        v-for="(member, i) in mentionCandidates"
        :key="member.member_id"
        :ref="(el) => (mentionItemEls[i] = el)"
        class="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm"
        :class="i === highlightIndex ? 'bg-accent' : ''"
        @mouseenter="highlightIndex = i"
        @mousedown.prevent="pickMention(member)"
      >
        <ChatAvatar :author="member" size="sm" />
        <span class="shrink-0 font-medium">@{{ member.nickname }}</span>
        <span class="min-w-0 flex-1 truncate text-muted-foreground">{{ member.display_name }}</span>
        <span v-if="memberRole(member)" class="shrink-0 text-xs text-muted-foreground">
          {{ memberRole(member) }}
        </span>
      </button>
    </div>

    <div v-if="draftLocation || attachments.length" class="mb-1.5 flex flex-wrap gap-1.5">
      <span
        v-if="draftLocation"
        class="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
      >
        <MapPin class="size-3" />
        {{ draftLocation[1].toFixed(4) }}, {{ draftLocation[0].toFixed(4) }}
        <button title="Remove location" @click="emit('clear-location')">
          <X class="size-3" />
        </button>
      </span>
      <template v-for="(attachment, i) in attachments" :key="i">
        <span v-if="attachment.type === 'image'" class="relative">
          <img
            :src="attachment.variant_urls.thumbnail"
            :alt="attachment.filename"
            :title="attachment.filename"
            class="size-10 rounded-md border object-cover"
          />
          <button
            class="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full border bg-background"
            title="Remove attachment"
            @click="removeAttachment(i)"
          >
            <X class="size-2.5" />
          </button>
        </span>
        <span
          v-else
          class="inline-flex max-w-full items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-xs font-medium"
        >
          <ChartLine v-if="attachment.type === 'metric'" class="size-3 shrink-0" />
          <MapPinned v-else class="size-3 shrink-0" />
          <span class="truncate">{{ attachmentLabel(attachment) }}</span>
          <button title="Remove attachment" @click="removeAttachment(i)">
            <X class="size-3" />
          </button>
        </span>
      </template>
    </div>

    <input
      ref="fileInputEl"
      type="file"
      :accept="imageAccept"
      class="hidden"
      @change="onFileSelect"
    />

    <div class="flex items-center gap-1.5">
      <button
        v-if="!dataAttachments"
        type="button"
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        title="Attach image"
        @click="openFileDialog"
      >
        <Paperclip class="size-4" />
      </button>
      <DropdownMenu v-else>
        <DropdownMenuTrigger as-child>
          <button
            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            title="Attach project data"
          >
            <Paperclip class="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="top">
          <DropdownMenuItem @click="openFileDialog">
            <ImagePlus class="mr-2 size-4" />
            Upload image
          </DropdownMenuItem>
          <DropdownMenuItem v-if="pinOption" @click="emit('pin-location')">
            <MapPin class="mr-2 size-4" />
            Location
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <EmojiPicker @update:model-value="insertEmoji">
        <button
          type="button"
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          title="Add emoji"
        >
          <Smile class="size-4" />
        </button>
      </EmojiPicker>

      <div class="relative min-w-0 flex-1">
        <textarea
          ref="textareaEl"
          v-model="text"
          rows="1"
          :placeholder="placeholder"
          class="block h-10 w-full resize-none rounded-full border bg-muted/50 py-2.5 pl-4 pr-11 text-sm leading-5 placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          @input="updateMention"
          @click="updateMention"
          @keydown="onKeydown"
          @paste="onPaste"
        ></textarea>
        <button
          class="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-white transition-opacity disabled:opacity-40"
          :disabled="!text.trim() && !attachments.length"
          title="Send message"
          @click="send"
        >
          <ArrowUp class="size-4" />
        </button>
      </div>
    </div>
  </div>
</template>
